<template>
    <v-dialog v-model="localShow" max-width="680" scrollable>
        <v-card class="rounded-modal" elevation="2">
            <v-card-title>
                <span class="headline">Script</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="localShow = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text>
                <v-alert v-if="loading" type="info" outlined class="mb-4">
                    Cargando scripts...
                </v-alert>

                <v-alert v-else-if="!scripts.length" type="warning" outlined class="mb-4">
                    No hay scripts disponibles para esta campaña/rol.
                </v-alert>

                <v-data-table v-else :headers="headers" :items="scripts" dense class="elevation-0" hide-default-footer>
                    <template #item.ver="{ item }">
                        <v-icon small class="mr-2" @click="open(item)">mdi-eye</v-icon>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Visor del script -->
        <v-dialog v-model="viewerOpen" max-width="980" scrollable :retain-focus="false" attach="body">
            <v-card class="rounded-modal" style="max-height: 85vh;" elevation="2">
                <v-card-title>
                    <span class="headline">{{
                        (currentScript && currentScript.nombre) || "Script"
                    }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon @click="viewerOpen = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="script-html">
                    <div v-html="(currentScript && currentScript.html) || ''"></div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { getAvailableScripts } from "@/services/scripts/scripts.service.js";
import {
    getCampaigsByLeadUuid,
    getOrigenLeadByUUID,
} from "@/services/leads/leads.service.js";

export default {
    name: "ShowScriptsDialog",
    props: {
        show: { type: Boolean, default: false },
        leadCampaings: { type: Array, default: () => [] },
        rolId: { type: String, required: true },
        motivo: { type: String, default: "PRIMER_LEAD" },
        leadUuid: { type: String, required: true },
    },
    data() {
        return {
            localShow: this.show,
            headers: [
                { text: "Script", value: "nombre" },
                { text: "Ver", value: "ver", sortable: false, width: 80 },
            ],
            scripts: [],
            loading: false,
            viewerOpen: false,
            currentScript: null,
        };
    },
    watch: {
        show: {
            immediate: true,
            handler(v) {
                this.localShow = v;
                if (v) this.fetch();
            },
        },
        localShow(v) {
            if (!v) this.$emit("update:show", false);
        },
    },
    methods: {
        async fetch() {
            this.loading = true;
            try {
                const leadUuid = this.leadUuid || this.$route.params.uuid;

                let medio = null;
                let campaniaId = null;

                const origenUuid = this.$route?.query?.origenLeadUuid || null;
                if (origenUuid) {
                    try {
                        const origen = await getOrigenLeadByUUID(origenUuid);
                        medio = origen?.medio || null;
                        campaniaId =
                            origen?.campania_id != null ? Number(origen.campania_id) : null;
                    } catch (_) {
                        // si falla se sigue con los fallbacks de siempre
                    }
                }

                if (!medio && campaniaId == null) {
                    try {
                        const origins = await getCampaigsByLeadUuid(leadUuid);
                        const arr = Array.isArray(origins) ? origins : [];
                        arr.sort(
                            (a, b) =>
                                new Date(b.fecha_lead || 0) - new Date(a.fecha_lead || 0)
                        );
                        const o = arr[0] || null;
                        medio = o?.medio || medio;
                        if (o?.campania_id != null) campaniaId = Number(o.campania_id);
                    } catch (_) {
                        // sin orígenes
                    }
                }

                if (!medio && this.leadCampaings?.length) {
                    const ordenadas = [...this.leadCampaings].sort(
                        (a, b) => new Date(b.fecha_lead || 0) - new Date(a.fecha_lead || 0)
                    );
                    const u = ordenadas[0];
                    medio = u?.medio || medio;
                    if (u?.campania_id != null) campaniaId = Number(u.campania_id);
                }

                const call = async (params) => {
                    const clean = Object.fromEntries(
                        Object.entries(params).filter(
                            ([, v]) => v !== null && v !== undefined && v !== ""
                        )
                    );
                    const resp = await getAvailableScripts(clean);
                    return Array.isArray(resp) ? resp : resp?.data || [];
                };

                const rolId = this.rolId || null;
                const motivo = this.motivo || "PRIMER_LEAD";
                let list = [];

                if (campaniaId != null) {
                    list = await call({
                        motivo,
                        rolId,
                        campaniaId,
                        includeGenerales: false,
                    });
                }

                if (!list.length && medio) {
                    list = await call({ motivo, rolId, medio, includeGenerales: false });
                }

                if (!list.length && rolId) {
                    list = await call({ motivo, rolId, includeGenerales: true });
                }

                // Ultimo fallback: solo motivo y generales
                if (!list.length) {
                    list = await call({ motivo, includeGenerales: true });
                }

                // De-dup por id
                const seen = new Set();
                this.scripts = list.filter(
                    (s) => !seen.has(s.id) && (seen.add(s.id), true)
                );
            } catch (e) {
                console.error("Error cargando scripts disponibles", e);
                this.scripts = [];
            } finally {
                this.loading = false;
            }
        },

        open(item) {
            this.currentScript = item;
            this.$nextTick(() => (this.viewerOpen = true));
        },
    },
};
</script>

<style scoped>
.script-html {
    max-height: 70vh;
    overflow: auto;
    background: #ffffff;
}

.rounded-modal {
    border-radius: 16px !important;
    overflow: hidden;
}

:deep(.v-dialog .v-card.rounded-modal) {
    border-radius: 16px !important;
}
</style>